import { createContext, useContext, useEffect, useState } from 'react'
import type { ReactNode } from 'react'
import type { Player, UserContextType } from '../types'
import { useAuth } from './useAuth'
import { supabase } from '../utils/supabase'

const UserContext = createContext<UserContextType | undefined>(undefined)

export function UserProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth()
  const [selectedPlayer, setSelectedPlayerState] = useState<Player | null>(null)
  const [userTeamId, setUserTeamId] = useState<number | null>(null)
  const [isLoading, setIsLoading] = useState(true)

  // Load user's selected player preference
  useEffect(() => {
    const loadUserPreference = async () => {
      if (!user) {
        setSelectedPlayerState(null)
        setUserTeamId(null)
        setIsLoading(false)
        return
      }

      try {
        const { data, error } = await supabase
          .from('user_preferences')
          .select('selected_player_id, team_id, team_manually_set')
          .eq('user_id', user.id)
          .single()

        if (error && error.code !== 'PGRST116') {
          // PGRST116 is "not found" error, which is expected for new users
          console.error('Error loading user preference:', error)
          setIsLoading(false)
          return
        }

        if (data?.team_id) {
          setUserTeamId(data.team_id)
        }

        if (data?.selected_player_id) {
          // Load the player details
          const { data: playerData, error: playerError } = await supabase
            .from('bowlers')
            .select('bowler_id, bowler_name, bowler_name_url_formated, gender, team_name, team_id')
            .eq('bowler_id', data.selected_player_id)
            .single()

          if (playerError) {
            console.error('Error loading player data:', playerError)
          } else if (playerData) {
            setSelectedPlayerState(playerData)
          }
        }
      } catch (error) {
        console.error('Error in loadUserPreference:', error)
      } finally {
        setIsLoading(false)
      }
    }

    loadUserPreference()
  }, [user])



  const setSelectedPlayer = async (player: Player) => {
    if (!user) return

    setSelectedPlayerState(player)

    try {
      // First, check if team is manually set
      const { data: currentPrefs } = await supabase
        .from('user_preferences')
        .select('team_manually_set')
        .eq('user_id', user.id)
        .single()

      const teamManuallySet = currentPrefs?.team_manually_set || false

      // Prepare update data
      const updateData: {
        user_id: string
        selected_player_id: number
        team_id?: number | null
        updated_at: string
      } = {
        user_id: user.id,
        selected_player_id: player.bowler_id,
        updated_at: new Date().toISOString(),
      }

      // Only auto-set team if not manually set and player has a team
      if (!teamManuallySet && player.team_id) {
        updateData.team_id = player.team_id
        setUserTeamId(player.team_id)
      }

      // Upsert user preference
      const { error } = await supabase
        .from('user_preferences')
        .upsert(updateData, {
          onConflict: 'user_id',
        })

      if (error) {
        console.error('Error saving user preference:', error)
      }
    } catch (error) {
      console.error('Error in setSelectedPlayer:', error)
    }
  }

  const value: UserContextType = {
    user,
    selectedPlayer,
    setSelectedPlayer,
    userTeamId,
    isLoading,
  }

  return <UserContext.Provider value={value}>{children}</UserContext.Provider>
}

// eslint-disable-next-line react-refresh/only-export-components
export function useUser() {
  const context = useContext(UserContext)
  if (context === undefined) {
    throw new Error('useUser must be used within a UserProvider')
  }
  return context
}